<template>
  <div class="p-6 rounded border border-black-30 sm:p-6 booking-widget">
    <h4 class="fs-20 font-medium flex-1 mb-2">
      Your stay at {{ supplier?.name }}
    </h4>
    <div class="mb-4">
      Enter your details and our reservation team will create a personalised
      quote for you.
    </div>

    <div class="mt-2 relative">
      <input
        class="input input-tall w-full"
        :class="{ 'input--error': formErrors.guestName }"
        placeholder="Guest Name"
        v-model="formData.guestName"
      />
    </div>
    <div class="mt-2 relative">
      <input
        class="input input-tall w-full"
        :class="{ 'input--error': formErrors.email }"
        placeholder="Email"
        v-model="formData.email"
      />
    </div>
    <div class="mt-2 relative">
      <input
        class="input input-tall w-full"
        :class="{ 'input--error': formErrors.phone }"
        placeholder="Phone"
        v-model="formData.phone"
      />
    </div>

    <div class="mt-2">
      <tooltip
        name="ttDates"
        ref="ttDates"
        @show="handleHideTrengo"
        @hide="handleShowTrengo"
      >
        <template v-slot:trigger="{ toggle, isShowing }">
          <div class="flex items-center relative">
            <svg
              width="24"
              height="24"
              class="flex-none booking-widget-input-icon--left"
            >
              <use xlink:href="/img/sprite.svg#ic_calendar"></use>
            </svg>
            <input
              type="text"
              tabindex="100"
              readonly
              placeholder="Add travel dates"
              :value="dateDisplay"
              class="cursor-pointer rounded input-tall w-full border border-black-30 booking-widget-input"
              :class="{ 'force-focus': isShowing }"
              @click="isMobile() ? openMobileBookingWidget(1) : toggle()"
            />
          </div>
        </template>

        <template>
          <div
            class="booking-widget-datepicker-container px-5 pt-6 pb-6 bg-white rounded mt-2 md:rounded-none mdsm:p-0"
          >
            <date-range-picker v-model="formData.date"> </date-range-picker>
          </div>
        </template>
      </tooltip>
    </div>

    <div class="mt-2 relative">
      <tooltip name="ttGuests" ref="ttGuests">
        <template v-slot:trigger="{ toggle, isShowing }">
          <div class="flex items-center relative">
            <input
              type="text"
              tabindex="101"
              readonly
              class="cursor-pointer rounded border border-black-30 input-tall w-full pl-5"
              :class="{
                'input--error': validations.moreRoomsThanGuests,
                'force-focus': isShowing,
              }"
              placeholder="Add guests"
              :value="guestsDisplay"
              @click="isMobile() ? openMobileBookingWidget(2) : toggle()"
            />
            <svg
              width="24"
              height="24"
              class="flex-none booking-widget-input-icon--right pointer-events-none"
              :class="{ 'transform rotate-180 fill-secondary': isShowing }"
            >
              <use xlink:href="/img/sprite.svg#ic_down"></use>
            </svg>
          </div>
        </template>

        <template>
          <div class="px-5 pt-6 pb-6 bg-white rounded mt-2 left-0 right-0">
            <detailed-guest-picker v-model="formData.guests" />
          </div>
        </template>
      </tooltip>
    </div>

    <div class="mt-2 relative">
      <tooltip name="ttRooms" ref="ttRooms">
        <template v-slot:trigger="{ toggle, isShowing }">
          <div class="flex items-center relative">
            <input
              type="text"
              tabindex="102"
              readonly
              class="cursor-pointer rounded border border-black-30 input-tall w-full pl-5"
              :class="{
                'input--error': validations.moreRoomsThanGuests,
                'force-focus': isShowing,
              }"
              placeholder="Add rooms"
              :value="roomsDisplay"
              @click="isMobile() ? openMobileBookingWidget(3) : toggle()"
            />
            <svg
              width="24"
              height="24"
              class="flex-none booking-widget-input-icon--right pointer-events-none"
              :class="{ 'transform rotate-180 fill-secondary': isShowing }"
            >
              <use xlink:href="/img/sprite.svg#ic_down"></use>
            </svg>
          </div>
        </template>

        <template>
          <div class="px-5 pt-6 pb-6 bg-white rounded mt-2 left-0 right-0">
            <room-picker v-model="formData.rooms" />
          </div>
        </template>
      </tooltip>
    </div>

    <div class="mt-2 relative">
      <textarea
        class="input input-tall w-full"
        :class="{ 'input--error': formErrors.misc }"
        placeholder="Tell us your wishes..."
        v-model="formData.misc"
      />
    </div>

    <p v-cloak v-if="validations.moreRoomsThanGuests" class="text-danger mt-1">
      You have selected more rooms than guests, please adjust your selection
    </p>

    <div class="mt-4 md:text-center">
      <button
        type="button"
        tabindex="103"
        class="booking-widget-enquire btn btn-primary input-tall w-full"
        :disabled="isSubmitting"
        @click="handleSubmitEnquire"
      >
        {{ cta }}
      </button>
    </div>
    <trengo-link>
      <template v-slot="{ openChat }">
        <div class="mt-4 md:text-center">
          <button
            type="button"
            tabindex="103"
            class="btn btn-primary btn-outlined input-tall w-full"
            @click="handleChat(openChat)"
          >
            Chat with a travel expert
          </button>
        </div>
      </template>
    </trengo-link>

    <slot name="bookingterm" />

    <enquiry-direct-mobile-booking-widget
      v-model="formData"
      ref="mobileBookingWidget"
      class="mdsm:visible lg:hidden"
    >
    </enquiry-direct-mobile-booking-widget>
  </div>
</template>

<script>
import get from "lodash/get";
import { mapGetters } from "vuex";
import {
  getBookingDateDisplay,
  getDetailedBookingGuestsDisplay,
  getBookingRoomsDisplay,
} from "../../../helpers";
import Tooltip from "../Tooltip";
import TrengoLink from "../TrengoLink";
import DateRangePicker from "../DateRangePicker";
import DetailedGuestPicker from "./DetailedGuestPicker";
import PackagePicker from "./PackagePicker";
import RoomPicker from "./RoomPicker";
import EnquiryDirectMobileBookingWidget from "./EnquiryDirectMobileBookingWidget";
import CurrencyContext from "../CurrencyContext";

export default {
  components: {
    TrengoLink,
    Tooltip,
    DetailedGuestPicker,
    PackagePicker,
    EnquiryDirectMobileBookingWidget,
    DateRangePicker,
    RoomPicker,
    CurrencyContext,
  },
  props: {
    resortId: {
      type: [Number, String],
      required: true,
    },
    resortImage: {
      type: String,
      required: true,
    },
    destinationSku: {
      type: String,
      required: true,
    },
  },
  data() {
    const formData = {
      date: [null, null],
      guests: {
        divers: 0,
        studentDivers: 0,
        nonDivers: 0,
      },
      rooms: 0,
      guestName: "",
      email: "",
      phone: "",
      misc: "",
    };

    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has("from")) {
      formData.date[0] = moment(searchParams.get("from"), "YYYY-MM-DD", true);
    }
    if (searchParams.has("to")) {
      formData.date[1] = moment(searchParams.get("to"), "YYYY-MM-DD", true);
    }

    return {
      isSubmitting: false,
      formErrors: {
        guestName: false,
        email: false,
        phone: false,
        misc: false,
      },
      formData,
      supplier: {
        availableDays: null,
      },
      validations: {
        moreRoomsThanGuests: false,
      },
      priceBasis: null,
    };
  },
  mounted() {
    window.fetchSupplierInfo(this.resortId).then((response) => {
      this.priceBasis = get(response, "supplier.room_rate_basis");
      this.supplier = response.supplier;
    });
  },
  provide() {
    return {
      formData: this.formData,
      supplier: this.supplier,
    };
  },
  computed: {
    cta() {
      return "Get a quote";
    },
    ...mapGetters(["hbtFlowEnabled", "hbtFlowChatOnly"]),
    dateDisplay() {
      const [from, to] = this.formData.date;

      return getBookingDateDisplay(from, to).join(" — ");
    },
    guestsDisplay() {
      return getDetailedBookingGuestsDisplay(this.formData.guests);
    },
    roomsDisplay() {
      return getBookingRoomsDisplay(this.formData.rooms);
    },
  },
  methods: {
    isMobile() {
      return window.matchMedia("only screen and (max-width: 1200px)").matches;
    },
    handleHideTrengo() {
      toggleTrengoChat({ show: false });
    },
    handleShowTrengo() {
      toggleTrengoChat({ show: true });
    },
    checkBeforeSubmit() {
      const [from, to] = this.formData.date;
      const { divers, studentDivers, nonDivers } = this.formData.guests;
      const totalGuests = divers + nonDivers + studentDivers;
      const rooms = this.formData.rooms;
      const { guestName, email, phone } = this.formData;

      this.formErrors.guestName = Boolean(!guestName);
      this.formErrors.email = Boolean(!email);
      this.formErrors.phone = Boolean(!phone);

      if (!guestName || !email || !phone) {
        return false;
      }

      if (!from || !to) {
        if (this.isMobile()) {
          this.openMobileBookingWidget(1);
        } else {
          this.$refs.ttDates.show();
        }

        return false;
      } else if (totalGuests === 0) {
        if (this.isMobile()) {
          this.openMobileBookingWidget(2);
        } else {
          this.$refs.ttGuests.show();
        }

        return false;
      } else if (rooms === 0) {
        if (this.isMobile()) {
          this.openMobileBookingWidget(3);
        } else {
          this.$refs.ttRooms.show();
        }

        return false;
      }

      if (totalGuests > 0 && rooms > 0 && rooms > totalGuests) {
        this.validations.moreRoomsThanGuests = true;
        return false;
      } else {
        this.validations.moreRoomsThanGuests = false;
        return true;
      }
    },
    handleSubmitEnquire() {
      if (this.isSubmitting) {
        return;
      }
      const canSubmit = this.checkBeforeSubmit();

      if (canSubmit) {
        const [from, to] = this.formData.date;
        const { guestName, email, phone } = this.formData;
        const { divers, studentDivers, nonDivers } = this.formData.guests;
        const rooms = this.formData.rooms;
        const nameParts = guestName.split(" ");
        const firstName = nameParts[0];
        const lastName = nameParts.slice(1).join(" ");

        const payload = {
          destination_id: this.destinationSku,
          first_name: firstName,
          last_name: lastName || "unknown",
          email: email,
          phone: phone,
          check_in: from.format("YYYY-MM-DD"),
          check_out: to.format("YYYY-MM-DD"),
          resort_id: this.resortId,
          number_of_guest: divers + studentDivers + nonDivers,
          number_of_rooms: rooms,
          enquiry_type: "resort",
          url: window.location.href,
          image_url: this.resortImage,
          session: window.Craft.session,
        };

        if (this.formData.misc) {
          payload.enquiry_information = this.formData.misc;
        }

        this.isSubmitting = true;

        $.ajax({
          method: "POST",
          url: "/",
          data: {
            action: "zuBlu/form/submitEnquiryDirect",
            [window.Craft.csrfTokenName]: window.Craft.csrfTokenValue,
            formData: payload,
          },
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        })
          .done(() => {
            window.pushDataLayer("enquiry", {
              submit_location: "main_site",
              submit_type: "direct",
            });

            window.location.href = `/enquiry-success?user=${guestName}&resortId=${this.resortId}`;
          })
          .fail((e) => {
            window.pushDataLayer("zublu_cta", {
              cta_type: "enquiry",
              cta_value: "enquiry_error",
              cta_screen: window.location.pathname,
            });

            $.ajax({
              method: "POST",
              url:
                window.ZUBLU_VARIABLES.OCTOBOX_API +
                "/api/error-notification-enquiry",
              data: JSON.stringify({ enquiry: payload, err_message: e }),
              headers: {
                Authorization:
                  "Basic ZGV2ZWxvcG1lbnRAenVibHVkaXZpbmcuY29tOmRldjEyMzQ1",
                "Content-Type": "application/json",
              },
            })
              .done(() => {
                window.location.href = `/enquiry-success?user=${guestName}&resortId=${this.resortId}`;
              })
              .fail(console.error)
              .always(() => {
                this.isSubmitting = false;
              });
          })
          .always(() => {
            this.isSubmitting = false;
          });
      }
    },
    openMobileBookingWidget(step) {
      this.$refs.mobileBookingWidget.show(step);
    },
    handleChat(openChat) {
      toggleTrengoChat({ show: true });

      openChat();
    },
  },
};
</script>
